import ProgressBar from '@avalara/bat-apps-shared/client/components/ProgressBar';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SCol, SRow, SContainer } from '@avalara/skylab-sdk/react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import CybersourcePaymentPage from '../cybersource-payment';
import { setReturnUrl } from '../../store/account';
import { appendQueryParamToUrl } from '../helpers/redirect';
import { getMarketingDataFromUrl, getQuantityFromUrl, getConnectors } from '../../lib/side-effects';
import { validateDiscountCode } from '../../lib/side-effects/offers';
import { validateAndSelectConnector } from '../../lib/side-effects/offer-flow';
import { stepNames } from '../../lib/side-effects/progress-bar';
import { selectState } from '../../selectors';
import { isEmbeddedSelector } from '../../selectors/offer';
import { currentStepLabelSelector } from '../../selectors/progress-bar';
import { getSelectedPlan } from '../../selectors/order';
import { setDiscountCode, setOrderQuantity } from '../../store/order';
import PageNotFound from '../page-not-found';
import LoginPrompt from '../shared/login-prompt';
import { getPartnerSignupConnectorByCode } from '../../../shared/constants';
import { Pages } from '../../../shared/types/offers';
import CompanyForm from './forms/company';
import GetStartedForm from './forms/get-started';
import BuyButton from './buy-button';
import OfferDetails from './offer-details';
import OrderSummary from './order-summary';
import StandaloneSummary from './standalone-summary';
import ReviewOrder from './review-order';
import ContactInfo from '../contact-info/index';
import NeedAssistance from './need-assistance-afs';
import SelectConnectorContainer from './select-connector-container';
import { sanitizeReturnUrl } from '../../../shared/utils/url-validation';
const OfferComponent = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { account: { isLoggedIn }, offer, order: { isConnectorValidated }, data: { connectors }, } = useSelector(selectState);
    const currentStepLabel = useSelector(currentStepLabelSelector);
    const selectedPlan = useSelector(getSelectedPlan);
    const { adjustableQuantity, allowedUsers, attributionDefaults: { campaign_id: defaultCampaignId }, isPaymentRequired, offerFlow, pages, redirectUrl, showEmbeddedSideContent, usesConnectors, } = offer;
    const selectedCampaignId = (selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.campaignId) || defaultCampaignId;
    useEffect(() => {
        dispatch(getMarketingDataFromUrl(selectedCampaignId));
    }, [selectedCampaignId]);
    useEffect(() => {
        if (usesConnectors && !connectors.data.length) {
            dispatch(getConnectors());
        }
    }, [usesConnectors]);
    useEffect(() => {
        var _a;
        const searchParams = new URLSearchParams(location.search);
        const partner = (searchParams.get('partner') || '').toUpperCase();
        const connectorId = ((_a = getPartnerSignupConnectorByCode(partner)) === null || _a === void 0 ? void 0 : _a.connectorId) || searchParams.get('connectorId');
        dispatch(validateAndSelectConnector(connectorId));
    }, [usesConnectors, connectors.data.length > 0]);
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const returnUrl = searchParams.get('returnUrl');
        const newReturnUrl = sanitizeReturnUrl(returnUrl || '');
        dispatch(setReturnUrl(newReturnUrl));
        if (!adjustableQuantity)
            return;
        const quantity = getQuantityFromUrl(location.search);
        if (quantity != null)
            dispatch(setOrderQuantity({ quantity, offer }));
    }, []);
    const isEmbedded = useSelector(isEmbeddedSelector);
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const discountCode = searchParams.get('c');
        if (discountCode != null) {
            dispatch(setDiscountCode(discountCode));
            dispatch(validateDiscountCode(offerFlow, discountCode));
        }
    }, [isLoggedIn]);
    if (redirectUrl) {
        const searchParams = new URLSearchParams(location.search);
        return React.createElement(Redirect, { to: appendQueryParamToUrl(redirectUrl, searchParams.toString()) });
    }
    const onPaymentStep = currentStepLabel === stepNames.paymentMethod;
    const onCreateAccountStep = currentStepLabel === stepNames.createYourAccount;
    const isOnCreateAccountStep = onCreateAccountStep;
    const showOfferDetails = !isEmbedded && !onPaymentStep && offer.offerDetails;
    const mainColumnSpan = isEmbedded ? 'auto' : 'md-7 8';
    const showSideColumn = (!isEmbedded || showEmbeddedSideContent) && !isOnCreateAccountStep;
    const mainColumnCenter = undefined;
    if (!isLoggedIn && allowedUsers === 'EXISTING')
        return React.createElement(LoginPrompt, null);
    if (!offerFlow)
        return React.createElement(PageNotFound, null);
    if (usesConnectors === 'REQUIRED' && !isConnectorValidated)
        return null;
    const authenticatedRedirectPath = isPaymentRequired ? 'payment' : 'review-order';
    const showProgressbar = offerFlow !== 'avalara-for-shopify';
    return (React.createElement(SContainer, { role: 'main' },
        React.createElement(SRow, { className: 'pad-top-sm' },
            React.createElement(SCol, { span: mainColumnSpan, center: mainColumnCenter }, showProgressbar && React.createElement(ProgressBar, null))),
        React.createElement(SRow, null,
            React.createElement(SCol, { span: mainColumnSpan, center: mainColumnCenter },
                React.createElement(Switch, null,
                    !isLoggedIn && React.createElement(Route, { path: `/${offerFlow}/get-started` },
                        React.createElement(GetStartedForm, { flow: offerFlow })),
                    !isLoggedIn && React.createElement(Route, { path: `/${offerFlow}/company` },
                        React.createElement(CompanyForm, { flow: offerFlow })),
                    pages.includes(Pages.Payment) && React.createElement(Route, { path: `/${offerFlow}/payment` },
                        React.createElement(CybersourcePaymentPage, { paymentView: 'offer', isEmbedded: isEmbedded })),
                    pages.includes(Pages.AddBillingContact) && React.createElement(Route, { path: `/${offerFlow}/add-billing-contact` },
                        React.createElement(ContactInfo, null)),
                    pages.includes(Pages.ReviewOrder) && React.createElement(Route, { path: `/${offerFlow}/review-order` },
                        React.createElement(ReviewOrder, null)),
                    isEmbedded && !showEmbeddedSideContent && React.createElement(Route, { path: `/${offerFlow}/summary` },
                        React.createElement(StandaloneSummary, null)),
                    pages.includes(Pages.SelectConnector) && React.createElement(Route, { path: `/${offerFlow}/select-connector` },
                        React.createElement(SelectConnectorContainer, null)),
                    React.createElement(Route, null,
                        React.createElement(Redirect, { to: {
                                pathname: `/${offerFlow}/${isLoggedIn ? authenticatedRedirectPath : 'get-started'}`,
                                search: location.search,
                            } }))),
                showOfferDetails && React.createElement(OfferDetails, null)),
            showSideColumn && React.createElement(SCol, { span: 'md-5 4' },
                React.createElement(OrderSummary, null),
                (onPaymentStep || showEmbeddedSideContent) && React.createElement(BuyButton, null)),
            offerFlow === 'avalara-for-shopify' && React.createElement(NeedAssistance, null))));
};
export default OfferComponent;
